(function (root, factory) {
  'use strict';

  if (typeof module === 'object' && module.exports) {
    module.exports = factory;
    return;
  }

  if (root.CentralSportExpressDelivery && root.CentralSportExpressDelivery.namespace === 'cs-express-delivery') {
    return;
  }

  var configElement = root.document && root.document.querySelector('[data-cs-express-delivery-config]');
  var config = {};

  if (configElement) {
    try {
      config = JSON.parse(configElement.textContent || '{}');
    } catch (error) {
      console.warn('[CentralSport Express Delivery] Invalid configuration; defaults will be used.');
    }
  }

  root.CentralSportExpressDelivery = factory(config, root);
})(typeof window !== 'undefined' ? window : globalThis, function createExpressDeliveryEngine(userConfig, runtime) {
  'use strict';

  var TIME_ZONE = 'America/Lima';
  var NATIONAL_HOLIDAYS_2026 = [
    '2026-01-01', '2026-04-02', '2026-04-03', '2026-05-01',
    '2026-06-07', '2026-06-29', '2026-07-23', '2026-07-28',
    '2026-07-29', '2026-08-06', '2026-08-30', '2026-10-08',
    '2026-11-01', '2026-12-08', '2026-12-09', '2026-12-25'
  ];
  var DEFAULTS = {
    enabled: true,
    cutoff: '12:00',
    eligibilityTag: 'entrega-express-lima',
    blockedDates: '',
    nationalHolidays: NATIONAL_HOLIDAYS_2026,
    operationalBlackoutDates: '',
    capacityBlackoutDates: '',
    preHolidayMode: 'all_day',
    preHolidayTime: '12:00',
    developmentMode: false,
    maxBusinessDaySearch: 370
  };
  var STATUS = Object.freeze({
    TODAY: 'today',
    NEXT_BUSINESS_DAY: 'next_business_day',
    WEEKEND: 'weekend',
    HOLIDAY: 'holiday',
    BLACKOUT: 'blackout',
    ERROR: 'error',
    NOT_ELIGIBLE: 'not_eligible',
    UNAVAILABLE: 'unavailable',
    UNKNOWN: 'unknown'
  });
  var config = Object.assign({}, DEFAULTS, userConfig || {}, { timeZone: TIME_ZONE });
  var cutoff = parseCutoff(config.cutoff);
  var nationalHolidayResult = parseBlockedDates(config.nationalHolidays);
  var operationalBlackoutResult = parseBlockedDates(config.operationalBlackoutDates || config.blockedDates);
  var capacityBlackoutResult = parseBlockedDates(config.capacityBlackoutDates);
  var nationalHolidaySet = new Set(nationalHolidayResult.valid);
  var operationalBlackoutSet = new Set(operationalBlackoutResult.valid);
  var capacityBlackoutSet = new Set(capacityBlackoutResult.valid);
  var preHolidayTime = parseCutoff(config.preHolidayTime);
  var formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
    weekday: 'short'
  });
  var simulationState = {
    active: false,
    scope: 'real',
    instant: null,
    date: '',
    time: '',
    forcedState: 'automatic',
    error: ''
  };
  var activeSimulationController = null;
  var activeDiagnosticPanel = null;
  var pendingSimulationReset = null;

  var invalidConfiguredDates = nationalHolidayResult.invalid
    .concat(operationalBlackoutResult.invalid, capacityBlackoutResult.invalid);
  if (config.developmentMode && invalidConfiguredDates.length && typeof console !== 'undefined') {
    console.warn(
      '[CentralSport Express Delivery] Ignored invalid blocked dates:',
      invalidConfiguredDates.slice()
    );
  }

  function parseCutoff(value) {
    var match = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(String(value || '').trim());
    var parsed = match && {
      hour: Number(match[1]),
      minute: Number(match[2]),
      second: Number(match[3] || 0)
    };

    if (!parsed || parsed.hour > 23 || parsed.minute > 59 || parsed.second > 59) {
      return { hour: 12, minute: 0, second: 0, value: '12:00:00', valid: false };
    }

    parsed.value = [parsed.hour, parsed.minute, parsed.second]
      .map(function (part) { return String(part).padStart(2, '0'); })
      .join(':');
    parsed.valid = true;
    return parsed;
  }

  function parseBlockedDates(value) {
    var entries = Array.isArray(value) ? value : String(value || '').split(/[\n,;]+/);
    var valid = [];
    var invalid = [];

    entries.forEach(function (entry) {
      var normalized = String(entry || '').trim();
      if (!normalized) return;

      if (isValidCalendarDate(normalized)) {
        if (!valid.includes(normalized)) valid.push(normalized);
      } else {
        invalid.push(normalized);
      }
    });

    return { valid: valid, invalid: invalid };
  }

  function isValidCalendarDate(value) {
    var match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match) return false;

    var year = Number(match[1]);
    var month = Number(match[2]);
    var day = Number(match[3]);
    var date = new Date(Date.UTC(year, month - 1, day));

    return date.getUTCFullYear() === year
      && date.getUTCMonth() === month - 1
      && date.getUTCDate() === day;
  }

  function getLimaDateTime(input) {
    var instant = input instanceof Date ? new Date(input.getTime()) : new Date(input == null ? Date.now() : input);
    if (Number.isNaN(instant.getTime())) return null;

    var parts = {};
    formatter.formatToParts(instant).forEach(function (part) {
      if (part.type !== 'literal') parts[part.type] = part.value;
    });

    var year = Number(parts.year);
    var month = Number(parts.month);
    var day = Number(parts.day);
    var hour = Number(parts.hour);
    var minute = Number(parts.minute);
    var second = Number(parts.second);

    return {
      timeZone: TIME_ZONE,
      instant: instant,
      year: year,
      month: month,
      day: day,
      hour: hour,
      minute: minute,
      second: second,
      weekday: parts.weekday,
      date: formatCalendarDate(year, month, day),
      time: [hour, minute, second].map(function (part) { return String(part).padStart(2, '0'); }).join(':')
    };
  }

  function formatCalendarDate(year, month, day) {
    return [year, month, day].map(function (part, index) {
      return index === 0 ? String(part).padStart(4, '0') : String(part).padStart(2, '0');
    }).join('-');
  }

  function normalizeCalendarDate(input) {
    if (typeof input === 'string' && isValidCalendarDate(input)) return input;
    if (input && typeof input.date === 'string' && isValidCalendarDate(input.date)) return input.date;
    var limaDateTime = getLimaDateTime(input);
    return limaDateTime && limaDateTime.date;
  }

  function addCalendarDays(dateString, days) {
    if (!isValidCalendarDate(dateString)) return null;
    var parts = dateString.split('-').map(Number);
    var date = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2] + days));
    return formatCalendarDate(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
  }

  function isBlockedDate(input) {
    var dateString = normalizeCalendarDate(input);
    return dateString ? operationalBlackoutSet.has(dateString) : false;
  }

  function isNationalHoliday(input) {
    var dateString = normalizeCalendarDate(input);
    return dateString ? nationalHolidaySet.has(dateString) : false;
  }

  function isCapacityBlackout(input) {
    var dateString = normalizeCalendarDate(input);
    return dateString ? capacityBlackoutSet.has(dateString) : false;
  }

  function isWeekend(input) {
    var dateString = normalizeCalendarDate(input);
    if (!dateString) return false;
    var parts = dateString.split('-').map(Number);
    var dayOfWeek = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2])).getUTCDay();
    return dayOfWeek === 0 || dayOfWeek === 6;
  }

  function isPreHoliday(input) {
    var dateString = normalizeCalendarDate(input);
    return Boolean(dateString && isNationalHoliday(addCalendarDays(dateString, 1)));
  }

  function isAtOrAfterTime(limaDateTime, parsedTime) {
    if (!limaDateTime) return false;
    var currentSeconds = (limaDateTime.hour * 3600) + (limaDateTime.minute * 60) + limaDateTime.second;
    var targetSeconds = (parsedTime.hour * 3600) + (parsedTime.minute * 60) + parsedTime.second;
    return currentSeconds >= targetSeconds;
  }

  function getOperationalStatus(input) {
    var limaDateTime = input && input.timeZone === TIME_ZONE ? input : getLimaDateTime(input);
    var dateString = normalizeCalendarDate(input);
    if (!dateString) return { operational: false, reason: 'invalid_current_date' };
    if (isCapacityBlackout(dateString)) return { operational: false, reason: 'capacity' };
    if (isBlockedDate(dateString)) return { operational: false, reason: 'operational_blackout' };
    if (isNationalHoliday(dateString)) return { operational: false, reason: 'holiday' };
    if (isWeekend(dateString)) return { operational: false, reason: 'weekend' };
    if (isPreHoliday(dateString)) {
      if (config.preHolidayMode === 'all_day') return { operational: false, reason: 'pre_holiday' };
      if (config.preHolidayMode === 'from_time' && isAtOrAfterTime(limaDateTime, preHolidayTime)) {
        return { operational: false, reason: 'pre_holiday' };
      }
    }
    return { operational: true, reason: null };
  }

  function isOperationalDay(input) {
    var dateString = normalizeCalendarDate(input);
    if (!dateString || isCapacityBlackout(dateString) || isBlockedDate(dateString)
      || isNationalHoliday(dateString) || isWeekend(dateString)) return false;
    return !(isPreHoliday(dateString) && config.preHolidayMode === 'all_day');
  }

  function isBusinessDay(input) {
    return isOperationalDay(input);
  }

  function isBeforeCutoff(input) {
    var limaDateTime = input && input.timeZone === TIME_ZONE ? input : getLimaDateTime(input);
    if (!limaDateTime) return null;

    var currentSeconds = (limaDateTime.hour * 3600) + (limaDateTime.minute * 60) + limaDateTime.second;
    var cutoffSeconds = (cutoff.hour * 3600) + (cutoff.minute * 60) + cutoff.second;
    return currentSeconds < cutoffSeconds;
  }

  function getNextBusinessDay(input) {
    return getNextOperationalDay(input);
  }

  function getNextOperationalDay(input) {
    var dateString = normalizeCalendarDate(input);
    var limit = Math.max(1, Math.min(Number(config.maxBusinessDaySearch) || 370, 3660));
    if (!dateString) return null;

    for (var offset = 1; offset <= limit; offset += 1) {
      var candidate = addCalendarDays(dateString, offset);
      if (isOperationalDay(candidate)) return candidate;
    }

    return null;
  }

  function normalizeTags(tags) {
    if (Array.isArray(tags)) return tags.map(function (tag) { return String(tag).trim().toLowerCase(); });
    if (typeof tags === 'string') {
      return tags.split(',').map(function (tag) { return tag.trim().toLowerCase(); }).filter(Boolean);
    }
    return null;
  }

  function evaluateProductEligibility(product) {
    if (product && typeof product.eligible === 'boolean') {
      return { eligible: product.eligible, confirmed: true, reason: product.eligible ? 'eligible_tag' : 'missing_eligibility_tag' };
    }

    var tags = normalizeTags(product && product.tags);
    if (!tags) return { eligible: null, confirmed: false, reason: 'eligibility_unknown' };

    var eligibilityTag = String(config.eligibilityTag || DEFAULTS.eligibilityTag).trim().toLowerCase();
    var eligible = tags.includes(eligibilityTag);
    return { eligible: eligible, confirmed: true, reason: eligible ? 'eligible_tag' : 'missing_eligibility_tag' };
  }

  function evaluateVariantAvailability(variant, product) {
    if (variant && typeof variant.available === 'boolean') {
      return { available: variant.available, confirmed: true, reason: variant.available ? 'variant_available' : 'variant_unavailable' };
    }
    if (product && typeof product.available === 'boolean') {
      return { available: product.available, confirmed: true, reason: product.available ? 'product_available' : 'product_unavailable' };
    }
    return { available: null, confirmed: false, reason: 'availability_unknown' };
  }

  function buildResult(status, context) {
    var serviceAvailable = status === STATUS.TODAY || status === STATUS.NEXT_BUSINESS_DAY;
    var visible = typeof context.visible === 'boolean'
      ? context.visible
      : status !== STATUS.NOT_ELIGIBLE && status !== STATUS.UNAVAILABLE;
    return {
      status: status,
      state: status,
      visible: visible,
      service_available: serviceAvailable,
      serviceAvailable: serviceAvailable,
      eligible: context.eligible,
      available: context.available,
      availabilityConfirmed: context.availabilityConfirmed,
      beforeCutoff: context.beforeCutoff,
      isBusinessDay: context.isBusinessDay,
      currentDate: context.currentDate,
      estimatedDate: context.estimatedDate,
      nextOperationalDate: context.nextOperationalDate || context.estimatedDate,
      message: '',
      reason: context.reason,
      operationalReason: context.operationalReason || context.reason
    };
  }

  function resolveCalculatedDelivery(options) {
    options = options || {};
    var hasInjectedNow = Object.prototype.hasOwnProperty.call(options, 'now');
    var effectiveNow = hasInjectedNow
      ? options.now
      : (simulationState.active && !options.ignoreSimulation ? simulationState.instant : undefined);
    var now = getLimaDateTime(effectiveNow);
    var eligibility = evaluateProductEligibility(options.product);
    var availability = evaluateVariantAvailability(options.variant, options.product);
    var businessDay = now ? isBusinessDay(now.date) : false;
    var operationalStatus = now ? getOperationalStatus(now) : { operational: false, reason: 'invalid_current_date' };
    var beforeCutoff = now ? isBeforeCutoff(now) : null;
    var context = {
      eligible: eligibility.eligible,
      available: availability.available,
      availabilityConfirmed: availability.confirmed,
      beforeCutoff: beforeCutoff,
      isBusinessDay: businessDay,
      currentDate: now && now.date,
      estimatedDate: null,
      nextOperationalDate: now && getNextOperationalDay(now.date),
      reason: null,
      operationalReason: null,
      visible: undefined
    };

    if (!config.enabled) {
      context.reason = 'engine_disabled';
      context.visible = false;
      return buildResult(STATUS.UNKNOWN, context);
    }
    if (!now) {
      context.reason = 'invalid_current_date';
      return buildResult(STATUS.ERROR, context);
    }
    if (!operationalStatus.operational) {
      context.estimatedDate = context.nextOperationalDate;
      context.operationalReason = operationalStatus.reason;
      if (operationalStatus.reason === 'weekend') {
        context.reason = 'weekend';
        return buildResult(STATUS.WEEKEND, context);
      }
      if (operationalStatus.reason === 'holiday') {
        context.reason = 'holiday';
        return buildResult(STATUS.HOLIDAY, context);
      }
      context.reason = 'blackout';
      return buildResult(STATUS.BLACKOUT, context);
    }
    if (!eligibility.confirmed) {
      context.reason = eligibility.reason;
      return buildResult(STATUS.UNKNOWN, context);
    }
    if (!eligibility.eligible) {
      context.reason = eligibility.reason;
      context.visible = false;
      return buildResult(STATUS.NOT_ELIGIBLE, context);
    }
    if (availability.confirmed && !availability.available) {
      context.reason = availability.reason;
      context.visible = false;
      return buildResult(STATUS.UNAVAILABLE, context);
    }
    if (!availability.confirmed) {
      context.reason = availability.reason;
      return buildResult(STATUS.UNKNOWN, context);
    }
    if (businessDay && beforeCutoff) {
      context.estimatedDate = now.date;
      context.reason = 'eligible_before_cutoff';
      return buildResult(STATUS.TODAY, context);
    }

    context.estimatedDate = getNextOperationalDay(now.date);
    if (!context.estimatedDate) {
      context.reason = 'next_business_day_not_found';
      return buildResult(STATUS.UNKNOWN, context);
    }
    context.reason = 'cutoff_reached';
    return buildResult(STATUS.NEXT_BUSINESS_DAY, context);
  }

  function resolveDelivery(options) {
    options = options || {};
    var result = resolveCalculatedDelivery(options);
    var forcedState = simulationState.active && !options.ignoreSimulation
      ? simulationState.forcedState
      : 'automatic';
    if (!forcedState || forcedState === 'automatic') return result;

    var status = forcedState === 'hidden' ? STATUS.UNAVAILABLE : forcedState;
    var visible = forcedState !== 'hidden';
    var serviceAvailable = status === STATUS.TODAY || status === STATUS.NEXT_BUSINESS_DAY;
    return Object.assign({}, result, {
      status: status,
      state: status,
      visible: visible,
      service_available: serviceAvailable,
      serviceAvailable: serviceAvailable,
      reason: forcedState === 'hidden' ? 'simulation_hidden' : 'simulation_' + status,
      operationalReason: forcedState === 'hidden' ? 'simulation_hidden' : 'simulation_' + status
    });
  }

  function evaluateItems(items, options) {
    var list = Array.isArray(items) ? items : [];
    var now = options && options.now;
    if (!list.length) {
      return {
        status: STATUS.UNKNOWN,
        expressEligible: false,
        message: '',
        reason: 'empty_items',
        itemResults: []
      };
    }

    var itemResults = list.map(function (item) {
      return resolveDelivery({
        now: now,
        product: item.product || item,
        variant: item.variant
      });
    });
    var blockingPriority = [
      STATUS.NOT_ELIGIBLE, STATUS.UNAVAILABLE, STATUS.WEEKEND,
      STATUS.HOLIDAY, STATUS.BLACKOUT, STATUS.ERROR, STATUS.UNKNOWN
    ];
    var blockingStatus = blockingPriority.find(function (status) {
      return itemResults.some(function (result) { return result.status === status; });
    });

    if (blockingStatus) {
      var reasonByStatus = {};
      reasonByStatus[STATUS.NOT_ELIGIBLE] = 'contains_not_eligible_item';
      reasonByStatus[STATUS.UNAVAILABLE] = 'contains_unavailable_item';
      reasonByStatus[STATUS.WEEKEND] = 'weekend';
      reasonByStatus[STATUS.HOLIDAY] = 'holiday';
      reasonByStatus[STATUS.BLACKOUT] = 'blackout';
      reasonByStatus[STATUS.ERROR] = 'delivery_engine_error';
      reasonByStatus[STATUS.UNKNOWN] = 'contains_unconfirmed_item';
      return {
        status: blockingStatus,
        expressEligible: false,
        message: '',
        reason: reasonByStatus[blockingStatus],
        itemResults: itemResults
      };
    }

    var schedule = itemResults[0];
    return {
      status: schedule.status,
      expressEligible: true,
      currentDate: schedule.currentDate,
      estimatedDate: schedule.estimatedDate,
      message: schedule.message,
      reason: 'all_items_express_eligible',
      itemResults: itemResults
    };
  }

  function dispatchUpdate(detail) {
    if (!runtime || !runtime.document || typeof runtime.CustomEvent !== 'function') return;
    var publicResult = resolveDelivery({ product: { eligible: true, available: true } });
    renderDiagnosticPanel(activeDiagnosticPanel);
    runtime.document.dispatchEvent(new runtime.CustomEvent('cs:express-delivery:updated', {
      bubbles: true,
      detail: Object.assign({
        engine: api,
        result: publicResult,
        simulationActive: simulationState.active,
        simulationScope: simulationState.scope
      }, detail || {})
    }));
  }

  function setDiagnosticValue(panel, name, value) {
    var element = panel.querySelector('[data-cs-diagnostic-value="' + name + '"]');
    if (element) element.textContent = value == null || value === '' ? '—' : String(value);
  }

  function findSimulationController(scope) {
    if (!scope) return null;
    if (typeof scope.matches === 'function' && scope.matches('[data-cs-express-delivery-config]')) return scope;
    return typeof scope.querySelector === 'function'
      ? scope.querySelector('[data-cs-express-delivery-config]')
      : null;
  }

  function getSimulationInput(controller) {
    var controllerConfig = {};
    if (controller) {
      try {
        controllerConfig = JSON.parse(controller.textContent || '{}');
      } catch (error) {
        controllerConfig = {};
      }
    }
    if (!config.developmentMode || !controller || controllerConfig.simulationEnabled !== true) {
      return { active: false, instant: undefined, date: '', time: '', forcedState: 'automatic', error: '' };
    }

    var allowedStates = [
      'automatic', STATUS.TODAY, STATUS.NEXT_BUSINESS_DAY, STATUS.WEEKEND,
      STATUS.HOLIDAY, STATUS.BLACKOUT, STATUS.ERROR, 'hidden'
    ];
    var forcedState = controllerConfig.simulationState || 'automatic';
    if (!allowedStates.includes(forcedState)) {
      return {
        active: false,
        instant: undefined,
        date: '',
        time: '',
        forcedState: 'automatic',
        error: 'El estado simulado no es válido.'
      };
    }

    var date = controllerConfig.simulationDate || '';
    var time = controllerConfig.simulationTime || '';
    if (forcedState !== 'automatic' && (!date || !time)) {
      return {
        active: true,
        instant: undefined,
        date: '',
        time: '',
        forcedState: forcedState,
        error: ''
      };
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(time)) {
      return {
        active: false,
        instant: undefined,
        date: date,
        time: time,
        forcedState: forcedState,
        error: 'Completa la fecha YYYY-MM-DD y la hora HH:MM para simular.'
      };
    }

    var instant = new Date(date + 'T' + time + ':00-05:00');
    var limaDateTime = getLimaDateTime(instant);
    if (!limaDateTime || limaDateTime.date !== date || limaDateTime.time.slice(0, 5) !== time) {
      return {
        active: false,
        instant: undefined,
        date: date,
        time: time,
        forcedState: forcedState,
        error: 'La fecha u hora simulada no es válida.'
      };
    }

    return {
      active: true,
      instant: instant,
      date: date,
      time: time,
      forcedState: forcedState,
      error: ''
    };
  }

  function setSimulationState(input) {
    simulationState.active = Boolean(input && input.active);
    simulationState.scope = simulationState.active ? 'preview' : 'real';
    simulationState.instant = simulationState.active ? input.instant : null;
    simulationState.date = simulationState.active ? input.date : '';
    simulationState.time = simulationState.active ? input.time : '';
    simulationState.forcedState = simulationState.active ? (input.forcedState || 'automatic') : 'automatic';
    simulationState.error = input && input.error ? input.error : '';
  }

  function getSimulationState() {
    return {
      active: simulationState.active,
      scope: simulationState.scope,
      date: simulationState.date,
      time: simulationState.time,
      forcedState: simulationState.forcedState,
      error: simulationState.error
    };
  }

  function getSimulationLabel(state) {
    var labels = {
      automatic: 'Fecha y hora simuladas',
      today: 'Entrega hoy',
      next_business_day: 'Próximo día hábil',
      weekend: 'Fin de semana',
      holiday: 'Feriado',
      blackout: 'Blackout operativo',
      error: 'Error o fallback',
      hidden: 'Comunicación oculta'
    };
    return labels[state] || labels.automatic;
  }

  function renderDiagnosticPanel(panel) {
    if (!panel) return;
    var effectiveInstant = simulationState.active ? simulationState.instant : undefined;
    var limaDateTime = getLimaDateTime(effectiveInstant);
    var product = { eligible: true, available: true };
    var realResult = resolveDelivery({ product: product, ignoreSimulation: true });
    var result = resolveDelivery({ product: product });
    setDiagnosticValue(panel, 'engine', config.enabled ? 'Activado' : 'Desactivado');
    setDiagnosticValue(panel, 'scope', simulationState.active
      ? 'Vista simulada: ' + getSimulationLabel(simulationState.forcedState)
      : 'Usando reloj real');
    setDiagnosticValue(panel, 'date', limaDateTime && limaDateTime.date);
    setDiagnosticValue(panel, 'time', limaDateTime && limaDateTime.time);
    setDiagnosticValue(panel, 'timezone', TIME_ZONE);
    setDiagnosticValue(panel, 'cutoff', cutoff.value);
    setDiagnosticValue(panel, 'business-day', limaDateTime ? (isBusinessDay(limaDateTime.date) ? 'Sí' : 'No') : 'No disponible');
    setDiagnosticValue(panel, 'blocked', limaDateTime ? (isBlockedDate(limaDateTime.date) ? 'Sí' : 'No') : 'No disponible');
    setDiagnosticValue(panel, 'real-status', realResult.status);
    setDiagnosticValue(panel, 'status', result.status);
    setDiagnosticValue(panel, 'service-available', result.service_available ? 'Sí' : 'No');
    setDiagnosticValue(panel, 'visible', result.visible ? 'Sí' : 'No');
    setDiagnosticValue(panel, 'reason', result.reason);
    setDiagnosticValue(panel, 'operational-reason', result.operationalReason);
    setDiagnosticValue(panel, 'next-business-day', result.nextOperationalDate);
  }

  function findDiagnosticPanel(scope) {
    if (!scope) return null;
    if (typeof scope.matches === 'function' && scope.matches('[data-cs-express-delivery-diagnostic]')) return scope;
    return typeof scope.querySelector === 'function' ? scope.querySelector('[data-cs-express-delivery-diagnostic]') : null;
  }

  function resetSimulation(reason) {
    setSimulationState({ active: false, error: '' });
    renderDiagnosticPanel(activeDiagnosticPanel);
    dispatchUpdate({ reason: reason || 'simulation_reset' });
  }

  function initSimulationController(scope, publish) {
    var controller = findSimulationController(scope);
    if (!controller || !config.developmentMode) return null;
    if (pendingSimulationReset && runtime && typeof runtime.clearTimeout === 'function') {
      runtime.clearTimeout(pendingSimulationReset);
      pendingSimulationReset = null;
    }

    var simulationInput = getSimulationInput(controller);
    setSimulationState(simulationInput);
    activeSimulationController = controller;
    renderDiagnosticPanel(activeDiagnosticPanel);

    if (publish) {
      dispatchUpdate({
        reason: simulationState.active
          ? 'simulation_updated'
          : 'simulation_disabled',
        simulationError: simulationInput.error
      });
    }
    return controller;
  }

  function initDiagnosticPanel(scope) {
    var panel = findDiagnosticPanel(scope);
    if (!panel) return null;
    activeDiagnosticPanel = panel;
    renderDiagnosticPanel(panel);
    return panel;
  }

  var api = Object.freeze({
    namespace: 'cs-express-delivery',
    version: '1.3.0',
    timeZone: TIME_ZONE,
    config: Object.freeze(Object.assign({}, config, {
      cutoff: cutoff.value,
      blockedDates: operationalBlackoutResult.valid.slice(),
      nationalHolidays: nationalHolidayResult.valid.slice(),
      operationalBlackoutDates: operationalBlackoutResult.valid.slice(),
      capacityBlackoutDates: capacityBlackoutResult.valid.slice(),
      invalidBlockedDates: invalidConfiguredDates.slice(),
      preHolidayTime: preHolidayTime.value
    })),
    STATUS: STATUS,
    getLimaDateTime: getLimaDateTime,
    isBusinessDay: isBusinessDay,
    isOperationalDay: isOperationalDay,
    getOperationalStatus: getOperationalStatus,
    isNationalHoliday: isNationalHoliday,
    isBlockedDate: isBlockedDate,
    isCapacityBlackout: isCapacityBlackout,
    isPreHoliday: isPreHoliday,
    isBeforeCutoff: isBeforeCutoff,
    getNextBusinessDay: getNextBusinessDay,
    getNextOperationalDay: getNextOperationalDay,
    evaluateProductEligibility: evaluateProductEligibility,
    evaluateVariantAvailability: evaluateVariantAvailability,
    resolveDelivery: resolveDelivery,
    evaluateItems: evaluateItems,
    parseBlockedDates: parseBlockedDates,
    dispatchUpdate: dispatchUpdate,
    getSimulationState: getSimulationState,
    resetSimulation: function () { resetSimulation('simulation_reset'); },
    refreshDiagnostics: function () { initDiagnosticPanel(runtime && runtime.document); }
  });

  if (runtime && runtime.document) {
    if (runtime.document.readyState === 'loading') {
      runtime.document.addEventListener('DOMContentLoaded', function () {
        initSimulationController(runtime.document, false);
        initDiagnosticPanel(runtime.document);
        dispatchUpdate({ reason: 'ready' });
      }, { once: true });
    } else {
      initSimulationController(runtime.document, false);
      initDiagnosticPanel(runtime.document);
      dispatchUpdate({ reason: 'ready' });
    }
    runtime.document.addEventListener('maximize:cart:updated', function () {
      dispatchUpdate({ reason: 'cart_updated' });
    });
    runtime.document.addEventListener('shopify:section:load', function (event) {
      var scope = event.target || runtime.document;
      initSimulationController(scope, true);
      initDiagnosticPanel(scope);
    });
    runtime.document.addEventListener('shopify:section:unload', function (event) {
      var controller = findSimulationController(event.target);
      if (controller && controller === activeSimulationController) {
        activeSimulationController = null;
        if (runtime && typeof runtime.setTimeout === 'function') {
          pendingSimulationReset = runtime.setTimeout(function () {
            pendingSimulationReset = null;
            if (!activeSimulationController) resetSimulation('simulation_section_unloaded', null);
          }, 100);
        } else {
          resetSimulation('simulation_section_unloaded', null);
        }
      }
      var panel = findDiagnosticPanel(event.target);
      if (!panel || panel !== activeDiagnosticPanel) return;
      activeDiagnosticPanel = null;
    });
  }

  return api;
});
